/**
 * GroupRoute — Route guard that checks user group membership.
 *
 * Usage in App.js:
 *   <Route path="/admin/users/" element={
 *     <GroupRoute allowedGroups={['admin']}>
 *       <UserManagement />
 *     </GroupRoute>
 *   } />
 *
 * Behaviour:
 *   - Not authenticated → redirect to /login (saves requested path)
 *   - Authenticated but wrong group → redirect to /unauthorized
 *   - Authenticated + in an allowed group → renders children
 *
 * allowedGroups is an OR check: user must belong to AT LEAST ONE listed group.
 * superadmin is in both 'admin' and 'staff' groups (computed at login).
 */

import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from './components/user/AuthProvider';

const GroupRoute = ({ children, allowedGroups = [] }) => {
  const { isAuthenticated, groups } = useContext(AuthContext);
  const location = useLocation();

  if (!isAuthenticated) {
    localStorage.setItem('requestedPage', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (allowedGroups.length === 0) {
    // No group restriction — just requires authentication
    return children;
  }

  const userGroups = groups && groups.length > 0
    ? groups
    : (() => {
        // Fallback: read from localStorage if context hasn't hydrated yet
        try { return JSON.parse(localStorage.getItem('groups') || '[]'); } catch { return []; }
      })();

  const hasAccess = allowedGroups.some((g) => userGroups.includes(g));

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default GroupRoute;
